<script lang="ts">
    import ExpandDown from "@app/components/Icons/ExpandDown.svelte";
    import type {
        CardsetDto,
        CardsetSummaryDto,
    } from "@app/models/flashcardsApi.model";
    import {
        getFavoriteTranslations,
        getFavoriteTranslationsCardsets,
    } from "@app/services/flashcards.service";
    import MyFavoriteTranslationsCardsListWithFilter from "./MyFavoriteTranslationsCardsListWithFilter.svelte";
    import {
        COMMON__No_favorite_translations_found,
        COMMON__Search_for_translations,
        COMMON__To_view_favorite_translations_you_need_to_be_logged_in,
        COMMON__logIn,
        ERROR__networkError__message,
        FAVORITE__Learn_with_flashcards,
        SPEECH__tryAgain,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { sendFavoriteAnalyticsEvent } from "@app/services/analytics/analytics-send-event";
    import { favoriteTranslations } from "../FlashcardsIntegration.service";
    import {
    getCardsApplicationUrl,
        getLoginUrl,
        isLoggedIn,
    } from "@app/services/transferState-service";
    import Loading from "@app/components/Icons/Loading.svelte";
    const FLASHCARDS_APP_URL = getCardsApplicationUrl();
    const MIN_CARDS_IN_CARDSET_FOR_CTA_BELLOW_CARDS = 7;

    const urlParams = new URLSearchParams(window.location.search);
    const l1 = urlParams.get("l1");
    const l2 = urlParams.get("l2");

    interface CardsetSummaryWithData {
        active: boolean;
        summary: CardsetSummaryDto;
        getFullCardsetLazy: (
            obj: CardsetSummaryWithData
        ) => Promise<CardsetDto>;
        cardsetPromise?: Promise<CardsetDto>;
    }

    const isCardsetActiveFromRequestParams = (
        cardsetSummary: CardsetSummaryDto
    ) =>
        (cardsetSummary.languageCode1 === l1 &&
            cardsetSummary.languageCode2 === l2) ||
        (cardsetSummary.languageCode1 === l2 &&
            cardsetSummary.languageCode2 === l1);

    const cardsetsSummaryWithDataPromise = new Promise<
        CardsetSummaryWithData[]
    >((resolve, reject) => {
        const cardsetsSummariesPromise = getFavoriteTranslationsCardsets();
        cardsetsSummariesPromise
            .then((cardsetSummaries) => {
                let hasActiveCardset = false;
                const summariesWithData = cardsetSummaries
                    .filter((summary) => summary.cardsSize > 0)
                    .map((summary) => {
                        const summaryWithData = {
                            summary,
                            active: isCardsetActiveFromRequestParams(summary),
                            getFullCardsetLazy: (
                                obj: CardsetSummaryWithData
                            ) => {
                                if (!obj.cardsetPromise) {
                                    obj.cardsetPromise =
                                        getFavoriteTranslations({
                                            l1: summary.languageCode1 || "",
                                            l2: summary.languageCode2 || "",
                                        });
                                }
                                return obj.cardsetPromise;
                            },
                        };
                        hasActiveCardset =
                            hasActiveCardset || summaryWithData.active;
                        return summaryWithData;
                    });
                if (!hasActiveCardset && summariesWithData.length > 0) {
                    summariesWithData[0].active = true;
                }
                resolve(summariesWithData);
            })

            .catch(reject);
    });

    export const getFlashcardsUrl = (cardsetId: string) =>
        `${FLASHCARDS_APP_URL}/flashcards/${cardsetId}-${Date.now()}`;
</script>

{#await cardsetsSummaryWithDataPromise}
    <Loading />
{:then cardsets}
    {#if cardsets.length > 0}
        {#each cardsets as summaryWithData, summaryIndex}
            <h2>
                <button
                    type="button"
                    class="flex text-xl items-center justify-between w-full p-2 font-medium text-left text-slate-700 border-slate-200 hover:bg-slate-100"
                    class:border-t={summaryIndex > 0}
                    on:click={() => {
                        summaryWithData.active = !summaryWithData.active;
                    }}
                >
                    <span class="flex-1">{summaryWithData.summary.title}</span>
                    <ExpandDown
                        width="1.5rem"
                        height="1.5rem"
                        flipped={!summaryWithData.active}
                    />
                </button>
            </h2>
            {#if summaryWithData.active}
                {#await summaryWithData.getFullCardsetLazy(summaryWithData) then cardset}
                    <div class="pl-4">
                        <div class="flex justify-end pt-2">
                            <a
                                href={getFlashcardsUrl(cardset.id)}
                                target="_blank"
                                on:click={() =>
                                    sendFavoriteAnalyticsEvent({
                                        action: "learn",
                                    })}
                                class="inline-flex items-center justify-center px-4 py-2 font-medium text-center focus:ring-4focus:outline-none rounded-lg text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                                >{getMessage(
                                    FAVORITE__Learn_with_flashcards
                                )}</a
                            >
                        </div>
                        <MyFavoriteTranslationsCardsListWithFilter {cardset} />
                        {#if summaryWithData.summary.cardsSize >= MIN_CARDS_IN_CARDSET_FOR_CTA_BELLOW_CARDS}
                        <div class="flex justify-end">
                            <a
                                href={getFlashcardsUrl(cardset.id)}
                                target="_blank"
                                on:click={() =>
                                    sendFavoriteAnalyticsEvent({
                                        action: "learn",
                                    })}
                                class="inline-flex items-center justify-center px-4 py-2 font-medium text-center focus:ring-4 focus:outline-none rounded-lg text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                                >{getMessage(
                                    FAVORITE__Learn_with_flashcards
                                )}</a
                            >
                        </div>
                        {/if}
                    </div>
                {/await}
            {/if}
        {/each}
    {:else}
        <div>
            <p class="p-4 text-center text-xl">
                {getMessage(COMMON__No_favorite_translations_found)}
            </p>
            <p class="p-4 text-center text-xl">
                <a
                    href="/"
                    class="inline-flex items-center justify-center px-4 py-2 font-medium text-center focus:ring-4 focus:outline-none rounded-lg text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                    {getMessage(COMMON__Search_for_translations)}
                </a>
            </p>
            {#if !isLoggedIn()}
                <p class="p-4 text-center text-xl">
                    {getMessage(
                        COMMON__To_view_favorite_translations_you_need_to_be_logged_in
                    )}
                </p>
                <p class="p-4 text-center">
                    <a
                        href={getLoginUrl()}
                        class="inline-flex items-center justify-center px-4 py-2 font-medium text-center focus:ring-4 focus:outline-none rounded-lg text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                        >{getMessage(COMMON__logIn)}
                    </a>
                </p>
            {/if}
        </div>
    {/if}
{:catch e}
    {#if !isLoggedIn()}
        <p class="p-4 text-center text-xl">
            {getMessage(
                COMMON__To_view_favorite_translations_you_need_to_be_logged_in
            )}
        </p>
        <p class="p-4 text-center">
            <a
                href={getLoginUrl()}
                class="inline-flex items-center justify-center px-4 py-2 font-medium text-center focus:ring-4 focus:outline-none rounded-lg text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
            >
                {getMessage(COMMON__logIn)}
            </a>
        </p>
    {:else}
        <p class="p-4 text-center text-xl">
            {getMessage(ERROR__networkError__message)}
        </p>
        <p class="p-4 text-center">
            <button
                class="inline-flex items-center justify-center px-4 py-2 font-medium text-center focus:ring-4 focus:outline-none rounded-lg text-white bg-blue-700 hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                on:click={() => window.location.reload()}
                >{getMessage(SPEECH__tryAgain)}</button
            >
        </p>
    {/if}
{/await}
